/**
 * THE ONLY EVENTS THAT EXIST, AND THE ONLY VALUES THEY MAY CARRY.
 *
 * This module is an allowlist in both directions: a closed set of event names,
 * and for each one a closed set of parameter keys whose values come from
 * closed sets of enums. Anything else is REJECTED before it reaches the
 * adapter — not dropped silently at the adapter, not sanitised into something
 * plausible, not passed through with unknown keys stripped and the rest sent.
 *
 * WHY A VALIDATOR AND NOT A CONVENTION. Every leak of this kind is written by
 * someone who knew the rule. It arrives as a helpful extra parameter on an
 * existing call — a slug "just for debugging", an error string "so we can see
 * what failed", a service name because the funnel was hard to read. A rule in
 * a document does not fail a build. A rejection does.
 *
 * NOTHING HERE IS DERIVED FROM THE PAGE. No `location.href`, no `pathname`, no
 * `search`, no `hash`, no `document.title`, no route parameter, no GraphQL
 * value, no user input, no rendered copy. Callers pass a synthetic category
 * from `analytics/routes.ts` and an enum member; there is no parameter shaped
 * to hold free text, so there is nowhere for free text to go.
 */
import { CATEGORIES, type Category } from "./routes";
import { hasOwn } from "../../../../shared/lib/hasOwn";

/** Booking steps, as reported. Product's list, not the router's segments. */
export const STEPS = ["service", "time", "details", "review"] as const;
export type Step = (typeof STEPS)[number];

/** Booking outcomes, as reported. */
export const OUTCOMES = ["requested", "confirmed", "unavailable", "indeterminate"] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** Every event name this surface may ever send. */
export const EVENTS = [
  "page_view",
  "booking_profile_viewed",
  "booking_step_viewed",
  "booking_outcome_viewed",
] as const;
export type EventName = (typeof EVENTS)[number];

/**
 * The synthetic page fields.
 *
 * GA4 fills `page_location`, `page_title` and `page_referrer` FROM THE
 * DOCUMENT when they are not supplied — which is the single most likely way a
 * slug, a token or a query string reaches a report from a surface like this
 * one. So all three are always sent, always synthetic, and `page_referrer` is
 * always empty: an inbound referrer can itself be a credential-bearing URL
 * from another Lura page.
 *
 * The path is prefixed `/_c/` so a category can never be mistaken for, or
 * collide with, a professional's slug — the slug space is the bare root.
 */
export function syntheticPage(category: Category): {
  page_location: string;
  page_title: string;
  page_referrer: string;
} {
  return {
    page_location: `https://book.uselura.com/_c/${category}`,
    page_title: category,
    page_referrer: "",
  };
}

export type EventParams = Readonly<Record<string, unknown>>;

export type Validated =
  | { readonly ok: true; readonly name: EventName; readonly params: EventParams }
  | { readonly ok: false; readonly reason: string };

/** Keys each event may carry. Absent from this table means rejected. */
const ALLOWED_KEYS: Readonly<Record<EventName, readonly string[]>> = {
  page_view: ["page_location", "page_title", "page_referrer"],
  booking_profile_viewed: [],
  booking_step_viewed: ["step"],
  booking_outcome_viewed: ["outcome"],
};

function isEventName(name: string): name is EventName {
  return (EVENTS as readonly string[]).includes(name);
}

/**
 * Validate one event completely, or reject it with a reason.
 *
 * The reason is for a developer and a test, never for a payload. It names the
 * offending KEY but never echoes the offending VALUE — a rejection message
 * that quoted the value would put the very string being refused into a log,
 * and logs get pasted into issues.
 */
export function validateEvent(name: string, params: EventParams = {}): Validated {
  if (!isEventName(name)) return { ok: false, reason: `unknown event name` };

  const allowed = ALLOWED_KEYS[name];
  for (const key of Object.keys(params)) {
    if (!allowed.includes(key)) return { ok: false, reason: `unexpected parameter "${key}"` };
  }
  /*
   * SNAPSHOT THE OWN VALUES ONCE, AND VALIDATE THE SNAPSHOT.
   *
   * Two failures, both real, both found here.
   *
   * ONLY OWN KEYS COUNT. `Object.keys` lists own enumerable keys, so
   * `Object.create({step:"service"})` has none — it satisfies the allowlist
   * loop by having nothing in it — and then `params.step` reads "service"
   * through the prototype chain and validates. QE found that one.
   *
   * AND EACH VALUE IS READ EXACTLY ONCE. The first attempt at the fix read
   * through a helper at validation time and again when building the returned
   * copy, so a getter answering "confirmed" on the first read and a card
   * number on the second validated clean and then transmitted the card number.
   * My own test caught it, which is the only reason it is not in the branch.
   */
  const snapshot: Record<string, unknown> = {};
  for (const key of allowed) {
    if (hasOwn(params, key)) snapshot[key] = (params as Record<string, unknown>)[key];
  }
  const own = (key: string): unknown => snapshot[key];

  if (name === "booking_step_viewed") {
    const step = own("step");
    if (typeof step !== "string" || !(STEPS as readonly string[]).includes(step)) {
      return { ok: false, reason: `invalid step` };
    }
  }

  if (name === "booking_outcome_viewed") {
    const outcome = own("outcome");
    if (typeof outcome !== "string" || !(OUTCOMES as readonly string[]).includes(outcome)) {
      return { ok: false, reason: `invalid outcome` };
    }
  }

  if (name === "page_view") {
    for (const key of allowed) {
      if (typeof own(key) !== "string") return { ok: false, reason: `missing ${key}` };
    }
    // A page_view is the one event whose parameters are STRINGS rather than
    // enums, so it is the one that has to be checked for having been built
    // from the page instead of from a category. `syntheticPage` is the only
    // supported producer and these are its invariants.
    const title = own("page_title") as string;
    if (!(CATEGORIES as readonly string[]).includes(title)) {
      // A real document title is a string and would pass a type check. It has
      // to be a CATEGORY, or "we forgot to override it" ships a professional's
      // name as a page title.
      return { ok: false, reason: `page_title is not a category` };
    }
    if (own("page_location") !== `https://book.uselura.com/_c/${title}`) {
      return { ok: false, reason: `page_location is not this category's synthetic path` };
    }
    if (own("page_referrer") !== "") {
      return { ok: false, reason: `page_referrer must be empty` };
    }
  }

  /*
   * RETURN A FROZEN COPY, NOT THE CALLER'S OBJECT.
   *
   * `send` pushes whatever comes back into `dataLayer`. Handing back the same
   * reference means the thing that was validated and the thing that is
   * transmitted are the same mutable object — a getter that answers
   * "confirmed" on the first read, or a mutation after the call, both send
   * something nobody checked. QE demonstrated it. This module's own standard
   * is that "a boundary that trusts its callers is decoration", and returning
   * a reference is trusting the caller not to touch it afterwards.
   *
   * The copy is the snapshot, so it holds the same values that were checked —
   * not a second read of them.
   */
  return { ok: true, name, params: Object.freeze({ ...snapshot }) };
}
